import { useEffect, useRef, useState } from 'react';
import { PmCard } from '../../../components/pm';
import {
  Box, Typography, Button, Stack, TextField, Checkbox, IconButton,
  FormControl, InputLabel, Select, MenuItem, Grid, CircularProgress, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useProjektabschluss,
  type ChecklisteItem,
  type ProjektabschlussData,
  type AbschlussStatus,
} from '../../../api/hooks/useProjektabschluss';
import { useProject } from '../../../api/hooks/useProjects';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildProjektabschlussReport } from '../reports/projektabschlussReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';

const STATUSES: AbschlussStatus[] = ['offen', 'in_prep', 'abgenommen'];

function newId() {
  return `cl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Schritt 18 — Projektabschluss. Live-Speichern (Autosave). */
export function ProjektabschlussTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { data, isLoading, save } = useProjektabschluss(projectId);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [form, setForm] = useState<ProjektabschlussData>(data);
  const initialized = useRef(false);

  const autosave = useAutosave<ProjektabschlussData>({
    onSave: async (f) => { await save(f); },
  });

  useEffect(() => {
    if (!isLoading && !initialized.current) {
      setForm(data);
      initialized.current = true;
    }
  }, [isLoading, data]);

  const apply = (next: ProjektabschlussData) => {
    setForm(next);
    autosave.schedule(next);
  };

  const updateForm = <K extends keyof ProjektabschlussData>(key: K, value: ProjektabschlussData[K]) => {
    apply({ ...form, [key]: value });
  };

  const addChecklistItem = () => {
    const next: ChecklisteItem = { id: newId(), item: '', done: false, signed_by: '' };
    apply({ ...form, abnahme_checkliste: [...form.abnahme_checkliste, next] });
  };

  const updateChecklistItem = (id: string, patch: Partial<ChecklisteItem>) => {
    apply({
      ...form,
      abnahme_checkliste: form.abnahme_checkliste.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const removeChecklistItem = (id: string) => {
    apply({
      ...form,
      abnahme_checkliste: form.abnahme_checkliste.filter((c) => c.id !== id),
    });
  };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('abschluss.title', 'Projektabschluss') };
  const buildReport = () =>
    buildProjektabschlussReport({
      status: data.abschluss_status,
      datum: data.abschluss_datum,
      unterzeichner: data.abnahme_unterzeichner,
      lessonsPositiv: data.lessons_learned_positiv,
      lessonsNegativ: data.lessons_learned_negativ,
      checkliste: data.abnahme_checkliste.map((c) => ({
        item: c.item,
        done: c.done,
        signedBy: c.signed_by ?? null,
      })),
      uebergabeDoku: data.uebergabe_doku,
    });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  const datumValue: Dayjs | null = form.abschluss_datum ? dayjs(form.abschluss_datum) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">{t('abschluss.title', 'Projektabschluss')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('abschluss.subtitle', 'Lessons Learned, Abnahme, Übergabe-Dokumentation.')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PdfExportButton meta={pdfMeta} build={buildReport} />
            <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
          </Stack>
        </Stack>

        {/* Header-Zeile: Status / Datum / Unterzeichner */}
        <PmCard sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('abschluss.status', 'Status')}</InputLabel>
                <Select
                  label={t('abschluss.status', 'Status')}
                  value={form.abschluss_status}
                  onChange={(e) => updateForm('abschluss_status', e.target.value as AbschlussStatus)}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{t(`abschluss.status_${s}`, s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label={t('abschluss.datum', 'Abschlussdatum')}
                value={datumValue}
                onChange={(v) => updateForm('abschluss_datum', v ? v.format('YYYY-MM-DD') : null)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('abschluss.unterzeichner', 'Unterzeichner')}
                value={form.abnahme_unterzeichner}
                onChange={(e) => updateForm('abnahme_unterzeichner', e.target.value)}
              />
            </Grid>
          </Grid>
        </PmCard>

        {/* Lessons Learned */}
        <PmCard sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{t('abschluss.lessons', 'Lessons Learned')}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth multiline rows={6}
                label={t('abschluss.lessonsPositiv', 'Was lief gut?')}
                value={form.lessons_learned_positiv}
                onChange={(e) => updateForm('lessons_learned_positiv', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth multiline rows={6}
                label={t('abschluss.lessonsNegativ', 'Was sollte beim nächsten Mal anders laufen?')}
                value={form.lessons_learned_negativ}
                onChange={(e) => updateForm('lessons_learned_negativ', e.target.value)}
              />
            </Grid>
          </Grid>
        </PmCard>

        {/* Abnahme-Checkliste */}
        <PmCard sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">{t('abschluss.checkliste', 'Abnahme-Checkliste')}</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addChecklistItem}>
              {t('abschluss.checklisteAdd', 'Eintrag hinzufügen')}
            </Button>
          </Stack>
          {form.abnahme_checkliste.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t('abschluss.checklisteEmpty', 'Noch keine Einträge.')}
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={1}>
              {form.abnahme_checkliste.map((c) => (
                <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                  <Checkbox
                    checked={c.done}
                    onChange={(e) => updateChecklistItem(c.id, { done: e.target.checked })}
                    inputProps={{ 'aria-label': t('abschluss.checklisteDone', 'Erledigt') }}
                  />
                  <TextField
                    size="small" fullWidth
                    placeholder={t('abschluss.checklisteItem', 'Was abgenommen wird')}
                    value={c.item}
                    onChange={(e) => updateChecklistItem(c.id, { item: e.target.value })}
                  />
                  <TextField
                    size="small"
                    sx={{ minWidth: 200 }}
                    placeholder={t('abschluss.checklisteSignedBy', 'Unterzeichnet von')}
                    value={c.signed_by ?? ''}
                    onChange={(e) => updateChecklistItem(c.id, { signed_by: e.target.value })}
                  />
                  <IconButton
                    size="small" color="error"
                    onClick={() => removeChecklistItem(c.id)}
                    aria-label={tc('actions.delete', 'Löschen')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </PmCard>

        {/* Übergabe-Doku */}
        <PmCard>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{t('abschluss.uebergabe', 'Übergabe-Dokumentation')}</Typography>
          <TextField
            fullWidth multiline rows={10}
            placeholder={t('abschluss.uebergabePlaceholder', 'Markdown unterstützt. Verantwortliche, Betriebsübergabe, Schulungen, offene Punkte.')}
            value={form.uebergabe_doku}
            onChange={(e) => updateForm('uebergabe_doku', e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 14 } }}
          />
        </PmCard>
      </Box>
    </LocalizationProvider>
  );
}
